import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  communityProfileUrl?: string;
  linkedinUrl?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

export const PersonalInfoSection: React.FC<Props> = ({ values, updateField, errors }) => {
  return (
    <fieldset css={sectionBoxStyles}>
      <legend css={sectionTitleStyles}>Personal information</legend>

      <div css={twoColGridStyles}>
        <div css={fieldStyles}>
          <label css={labelStyles} htmlFor="first-name">
            First name <span css={requiredStyles}>*</span>
          </label>
          <Textfield
            id="first-name"
            value={values.firstName}
            onChange={(e) => updateField('firstName', (e.target as HTMLInputElement).value)}
          />
          {errors?.firstName && <div css={errorStyles}>{errors.firstName}</div>}
        </div>

        <div css={fieldStyles}>
          <label css={labelStyles} htmlFor="last-name">
            Last name <span css={requiredStyles}>*</span>
          </label>
          <Textfield
            id="last-name"
            value={values.lastName}
            onChange={(e) => updateField('lastName', (e.target as HTMLInputElement).value)}
          />
          {errors?.lastName && <div css={errorStyles}>{errors.lastName}</div>}
        </div>
      </div>

      <div css={fieldTopStyles}>
        <label css={labelStyles} htmlFor="email">
          Contact email <span css={requiredStyles}>*</span>
        </label>
        <Textfield
          id="email"
          type="email"
          value={values.email}
          onChange={(e) => updateField('email', (e.target as HTMLInputElement).value)}
        />
        {errors?.email && <div css={errorStyles}>{errors.email}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={labelStyles} htmlFor="community-profile-url">
          Atlassian Community profile URL <span css={requiredStyles}>*</span>
        </label>
        <Textfield
          id="community-profile-url"
          placeholder="https://community.atlassian.com/user/profile/<unique numbers>"
          value={values.communityProfileUrl}
          onChange={(e) => updateField('communityProfileUrl', (e.target as HTMLInputElement).value)}
        />
        {errors?.communityProfileUrl && <div css={errorStyles}>{errors.communityProfileUrl}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={labelStyles} htmlFor="linkedin-url">
          LinkedIn profile URL <span css={requiredStyles}>*</span>
        </label>
        <Textfield
          id="linkedin-url"
          placeholder="https://www.linkedin.com/in/yourprofileid/"
          value={values.linkedinUrl}
          onChange={(e) => updateField('linkedinUrl', (e.target as HTMLInputElement).value)}
        />
        {errors?.linkedinUrl && <div css={errorStyles}>{errors.linkedinUrl}</div>}
      </div>
    </fieldset>
  );
};

const sectionBoxStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: token('space.300'),
  marginBottom: token('space.400'),
  backgroundColor: token('elevation.surface'),
});

const sectionTitleStyles = css({
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '12px',
  letterSpacing: '0.04em',
  marginBottom: token('space.200'),
});

const twoColGridStyles = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: token('space.200'),
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.200'),
});

const labelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
  marginBottom: token('space.050'),
});

const requiredStyles = css({
  color: token('color.text.danger'),
});

const errorStyles = css({
  color: token('color.text.danger'),
  fontSize: '12px',
  marginTop: token('space.050'),
});
